import { useCallback, useEffect, useMemo, useState } from 'react';
import { Phone, MessageCircle, Truck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { clienteMobileService } from '@/services/clienteMobileService';
import { openClienteWhatsApp, CLIENTE_WHATSAPP_NUMBERS } from '@/utils/clienteWhatsApp';

function def(v: string | null | undefined, fallback: string): string {
  const d = v?.replace(/\D/g, '') ?? '';
  return d.length >= 10 ? d : fallback.replace(/\D/g, '');
}

export function ClienteAtendimentosPage() {
  const [centraisData, setCentraisData] = useState<{
    phoneEmergency: string | null;
    phoneWorkshop: string | null;
    phoneFinancial: string | null;
    phoneCustomerService: string | null;
    phoneTowRequest: string | null;
    phoneSos: string | null;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await clienteMobileService.getCentraisAtendimento();
      setCentraisData(data);
    } catch {
      setCentraisData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const centrais = useMemo(() => {
    const d = centraisData;
    return [
      { title: 'Oficina', phone: def(d?.phoneWorkshop, '84981312924'), gradient: 'from-orange-500 to-orange-700', text: 'text-white' },
      {
        title: 'Atendimento ao Cliente',
        phone: def(d?.phoneCustomerService, '8430263950'),
        gradient: 'from-amber-400 to-amber-600',
        text: 'text-black',
      },
      {
        title: 'Financeiro',
        phone: def(d?.phoneFinancial, '11911975992'),
        gradient: 'from-green-600 to-green-800',
        text: 'text-white',
      },
      {
        title: 'SOS / Central 24h',
        phone: def(d?.phoneSos, CLIENTE_WHATSAPP_NUMBERS.SOS.replace(/\D/g, '')),
        gradient: 'from-rose-700 to-red-900',
        text: 'text-white',
      },
    ];
  }, [centraisData]);

  const waCentral = (title: string, phone: string) => {
    openClienteWhatsApp(phone, `Olá, gostaria de falar sobre: ${title}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold mb-1">FAC — Fale com a gente</h1>
        <p className="text-sm text-muted-foreground">Centrais da sua unidade (quando cadastradas no sistema).</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {centrais.map((c) => (
          <Card key={c.title} className={`overflow-hidden bg-gradient-to-br ${c.gradient} border-0`}>
            <CardContent className={`pt-5 pb-5 ${c.text}`}>
              <p className="font-bold text-lg">{c.title}</p>
              <p className="text-sm opacity-90 mt-1">{c.phone}</p>
              <div className="flex gap-2 mt-4">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="bg-white/20 hover:bg-white/30 text-inherit border-0"
                  onClick={() => waCentral(c.title, c.phone)}
                >
                  <MessageCircle className="h-4 w-4 mr-1" />
                  WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-2">Guincho e emergência</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-auto py-4 justify-start"
            onClick={() => openClienteWhatsApp(CLIENTE_WHATSAPP_NUMBERS.GUINCHO, 'Olá! Gostaria de solicitar um guincho.')}
          >
            <Truck className="h-5 w-5 mr-2 text-amber-600" />
            Solicitar guincho
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-auto py-4 justify-start border-red-300"
            onClick={() => openClienteWhatsApp(CLIENTE_WHATSAPP_NUMBERS.SOS, 'SOS! Preciso de ajuda urgente!')}
          >
            <Phone className="h-5 w-5 mr-2 text-red-600" />
            Central 24h
          </Button>
        </div>
      </div>
    </div>
  );
}
